import {API} from 'common/api';
import type {
  OnboardingState,
  OnboardingSummary,
  ResumeStatus,
  ResumeUploadURLResponse,
  TopicWeight,
} from 'iq-onboarding/types';

export async function getResumeUploadUrl(extension: string): Promise<ResumeUploadURLResponse> {
  return API.post('/v1/onboarding/resume-upload-url', {extension});
}

export async function uploadResumeToS3(
  uploadUrl: string,
  fields: Record<string, string>,
  file: File,
): Promise<void> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) form.append(key, value);
  // The file field must be appended last; the browser sets the multipart Content-Type itself.
  form.append('file', file);

  const response = await fetch(uploadUrl, {method: 'POST', body: form});
  if (!response.ok) {
    // S3 returns 403 EntityTooLarge when the file exceeds the policy's size limit.
    if (response.status === 403) {
      throw new Error('Your resume is too large. Please upload a PDF under 20MB.');
    }
    throw new Error(`Upload failed: ${response.statusText}`);
  }
}

export async function processResume(resumeUuid: string): Promise<void> {
  // The API wrapper only throws on 5xx; a 4xx (resume not found / not in a pending
  // state) comes back as an {error} body. The success response carries resume_uuid,
  // so treat its absence as a failure and throw — otherwise the caller advances into
  // an endless "processing" poll for a job that never started.
  const res = (await API.post('/v1/onboarding/process-resume', {
    resume_uuid: resumeUuid,
  })) as {resume_uuid?: string; error?: string};
  if (!res?.resume_uuid) {
    throw new Error(res?.error || 'Could not start resume processing. Please try again.');
  }
}

export async function getResumeStatus(resumeUuid: string): Promise<ResumeStatus> {
  return API.get(`/v1/onboarding/resume-status/${resumeUuid}`);
}

export async function saveOnboarding(state: OnboardingState): Promise<void> {
  await API.post('/v1/onboarding', {
    resume_uuid: state.resumeUuid,
    positions: state.positions,
    companies: state.companies,
  });
}

// getTargetSkillset returns the target skillset for the given target position and
// company slugs — used to update the radar as selections change. Slugs are passed as
// repeated query params (?positions=a&positions=b&companies=google).
export async function getTargetSkillset(
  positionSlugs: string[],
  companySlugs: string[],
): Promise<TopicWeight[]> {
  const params = new URLSearchParams();
  for (const slug of positionSlugs) params.append('positions', slug);
  for (const slug of companySlugs) params.append('companies', slug);

  const res = (await API.get(`/v1/onboarding/target-skillset?${params.toString()}`)) as {
    skillset: TopicWeight[];
  };
  return res.skillset ?? [];
}

// getOnboardingSummary returns the guides + questions preview for the signup summary step.
// Slugs are passed as repeated query params (?companies=google&positions=data-scientist).
export async function getOnboardingSummary(
  companySlugs: string[],
  positionSlugs: string[],
): Promise<OnboardingSummary> {
  const params = new URLSearchParams();
  for (const slug of companySlugs) params.append('companies', slug);
  for (const slug of positionSlugs) params.append('positions', slug);

  return API.get(`/v1/onboarding/summary?${params.toString()}`);
}
